import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Alert, AlertType } from '../../services/data';
import Card from '../ui/Card';
import clsx from 'clsx';

interface AlertCardProps {
  alert: Alert;
}

export default function AlertCard({ alert }: AlertCardProps) {
  const { t } = useTranslation();

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getAlertTypeLabel = (type: AlertType) => {
    return t(`alerts.types.${type}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('default', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusBadge = () => {
    if (alert.resolved_at) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
          <CheckCircle className="w-3.5 h-3.5" />
          <span>{t('alerts.resolved')}</span>
        </div>
      );
    }
    if (alert.acknowledged_at) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
          <Clock className="w-3.5 h-3.5" />
          <span>{t('alerts.acknowledged')}</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full">
        <AlertCircle className="w-3.5 h-3.5" />
        <span>{t('alerts.active')}</span>
      </div>
    );
  };

  return (
    <Link to={`/alerts/${alert.id}`}>
      <Card className="hover:shadow-lg transition-shadow">
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={clsx('p-2 rounded-lg', getSeverityColor(alert.severity))}>
                {getSeverityIcon(alert.severity)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  {getAlertTypeLabel(alert.alert_type)}
                </h3>
                <p className="text-sm text-gray-600 truncate">
                  {alert.probe_name} - {alert.logger_name}
                </p>
              </div>
            </div>
            {getStatusBadge()}
          </div>

          {alert.message && (
            <p className="text-sm text-gray-700 line-clamp-2">{alert.message}</p>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-gray-200 text-xs text-gray-500">
            <span>{alert.site_name}</span>
            <span>{formatDate(alert.triggered_at)}</span>
          </div>
        </div>
      </Card>
    </Link>
  );
}
